import { useAutoneTranslation } from '@autone/translations';
import { fNumber, fPercentage, fReadableCurrency } from '@autone/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { InfoIcon } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type PropsWithChildren } from 'react';

import { useDefaultCurrency } from '../../hooks';
import { AutoneTooltip } from '../Tooltip';
import { cn } from '../utils';

import { formatDate, isFiniteNumber } from './DataTableCells.helpers';

const DataTableCellContentVariants = cva('flex flex-col gap-1 w-full', {
  variants: {
    align: {
      left: 'items-start text-left',
      center: 'items-center',
      right: 'items-end text-right',
    },
  },
  defaultVariants: {
    align: 'left',
  },
});

type DataTableCellContentVariantsProps = VariantProps<
  typeof DataTableCellContentVariants
>;

interface DataTableCellContentProps
  extends HTMLAttributes<HTMLDivElement>,
    DataTableCellContentVariantsProps {}

export const DataTableCellContent = forwardRef<
  HTMLDivElement,
  DataTableCellContentProps
>(({ children, className, align, ...props }, ref) => {
  return (
    <div
      className={cn(DataTableCellContentVariants({ align }), className)}
      {...props}
      ref={ref}
    >
      {children}
    </div>
  );
});

export const DataTableCellTitle = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    className={cn('font-normal leading-none whitespace-normal', className)}
    {...props}
    ref={ref}
  >
    <DataTableCellOptional>{children}</DataTableCellOptional>
  </div>
));

export const DataTableCellTitleWithInfo = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { tooltipText: string }
>(({ children, className, tooltipText, ...props }, ref) => (
  <DataTableCellTitle
    className={cn('flex items-center gap-1', className)}
    ref={ref}
    {...props}
  >
    <DataTableCellOptional>{children}</DataTableCellOptional>
    <AutoneTooltip text={tooltipText}>
      <InfoIcon className="w-3.5 h-3.5" />
    </AutoneTooltip>
  </DataTableCellTitle>
));

export const DataTableCellSubtitle = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div
    className={cn(
      'text-xs font-extralight text-grey-60 leading-none whitespace-nowrap',
      className,
    )}
    {...props}
    ref={ref}
  >
    <DataTableCellOptional>{children}</DataTableCellOptional>
  </div>
));

export const DataTableCellOptional = ({ children }: PropsWithChildren) => {
  const { common } = useAutoneTranslation();

  if (!children) {
    return common('na');
  } else {
    return children;
  }
};

export const DataTableCellLongText = ({
  value,
  maxLength,
}: {
  value?: string | null;
  maxLength: number;
}) => {
  const { common } = useAutoneTranslation();

  if (!value) {
    return common('na');
  } else if (value.length <= maxLength) {
    return value;
  } else {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>{value.slice(0, maxLength) + '...'}</span>
          </TooltipTrigger>
          <TooltipContent side={'bottom'} asChild>
            <div
              className={
                'p-1 bg-white border border-grey-20 rounded-sm shadow-md'
              }
            >
              {value}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
};

export const DataTableCellUnits = ({ value }: { value?: number | null }) => {
  const { common } = useAutoneTranslation();

  return typeof value === 'number'
    ? common('units-value', { value, count: value })
    : common('na');
};

export const DataTableCellDate = ({
  value,
}: {
  value?: Date | string | null;
}) => {
  const { common, lang } = useAutoneTranslation();

  return formatDate(new Date(value ?? ''), lang, common);
};

export const DataTableCellDateRange = ({
  start,
  end,
}: {
  start?: string | null;
  end?: string | null;
}) => {
  const { common, lang } = useAutoneTranslation();

  if (start && end)
    return `${formatDate(new Date(start), lang, common)} → ${formatDate(new Date(end), lang, common)}`;
  if (start && !end)
    return `${formatDate(new Date(start), lang, common)} → ${common('infinity-symbol')}`;
  return common('na');
};

export const DataTableCellDateTime = ({ value }: { value?: string }) => {
  const { common, lang } = useAutoneTranslation();

  return formatDate(new Date(value ?? ''), lang, common);
};

export const DataTableCellPerc = ({ value }: { value?: number }) => {
  const { common } = useAutoneTranslation();

  return value === undefined || Number.isNaN(value)
    ? common('na')
    : fPercentage(value);
};

export const DataTableCellPercProjected = ({ value }: { value?: number }) => {
  const { common } = useAutoneTranslation();

  return isFiniteNumber(value)
    ? common('perc-projected', { value })
    : common('na');
};

export const DataTableCellPercAchieved = ({ value }: { value?: number }) => {
  const { common } = useAutoneTranslation();
  return isFiniteNumber(value)
    ? common('perc-already-achieved', { value })
    : common('na');
};

export const DataTableCellPercRemaining = ({ value }: { value?: number }) => {
  const { common } = useAutoneTranslation();

  return isFiniteNumber(value)
    ? common('perc-remaining', { value })
    : common('na');
};

export const DataTableCellCurrencyValue = ({
  value,
  currency,
}: {
  value?: number | null;
  currency?: string;
}) => {
  const { common } = useAutoneTranslation();
  const { defaultCurrency } = useDefaultCurrency();

  return value === undefined || value === null
    ? common('na')
    : fReadableCurrency(value, currency ?? defaultCurrency);
};

export const DataTableIntegerValue = ({ value }: { value?: number | null }) => {
  const { common } = useAutoneTranslation();
  return value == null ? common('na') : fNumber(value);
};

export const DataTableFloatValue = ({ value }: { value?: number | null }) => {
  const { common } = useAutoneTranslation();
  return value == null ? common('na') : fNumber(value, '0,0.00');
};

export const DataTableCellDecimalValue = ({
  value,
  dp = 2,
}: {
  value?: number | null;
  dp?: number;
}) => {
  const { common } = useAutoneTranslation();

  if (value === undefined || value === null) {
    return common('na');
  }

  return value.toFixed(dp);
};

export const DataTableCellPercentageCovered = ({
  value,
}: {
  value?: number;
}) => {
  const { common } = useAutoneTranslation();

  return isFiniteNumber(value)
    ? common('perc-covered', { value: value, format: '0.00' })
    : common('na');
};

export const DataTableCellBoolean = ({ value }: { value?: boolean }) => {
  const { common } = useAutoneTranslation();

  return value ? common('yes') : common('no');
};
